import type { PdlAffordabilityJourneyDto, PdlAffordabilityTraceStepDto } from '../api/admin';
import {
  INCOME_RANGE_LABELS,
  LOAN_CATEGORY_LABELS,
  SCORING_TABLE_DESCRIPTIONS,
} from './pdlAdjudicatorCopy';

export type PdlLegacyCalcBar = {
  id: string;
  label: string;
  sublabel: string | null;
  amount: number;
  kind: 'income' | 'raw' | 'cell_cap' | 'adjustment' | 'result';
  binding: boolean;
};

export type PdlLegacyGridCalculationModel = {
  monthlyIncome: number | null;
  scoringTable: number | null;
  incomeBand: number | null;
  customerTier: number | null;
  profileChips: string[];
  loanPct: number | null;
  loanPctLabel: string | null;
  gridRaw: number | null;
  afterCellMax: number | null;
  legacyLimit: number | null;
  bars: PdlLegacyCalcBar[];
  scaleMax: number;
  formulaLine: string | null;
};

function traceStep(
  trace: PdlAffordabilityTraceStepDto[] | undefined,
  code: string,
): PdlAffordabilityTraceStepDto | undefined {
  return trace?.find((s) => s.code === code);
}

function readTraceNumber(step?: PdlAffordabilityTraceStepDto | null): number | null {
  const v = step?.value;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() && Number.isFinite(Number(v))) return Number(v);
  return null;
}

function fmt(n: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(n);
}

function parseLoanPct(
  remarks: PdlAffordabilityJourneyDto['remarks'],
  income: number | null,
  gridRaw: number | null,
): number | null {
  const remark = remarks?.find((r) => r.code === 'LEGACY_PRA_GRID')?.message ?? '';
  const pctMatch = remark.match(/loan_pct\s+(\d+(?:\.\d+)?)\s*%/i);
  if (pctMatch) return Number(pctMatch[1]) / 100;

  if (income != null && gridRaw != null && income > 0) {
    const approx = gridRaw / income;
    const common = [0.3, 0.35, 0.4, 0.45, 0.5, 0.55, 0.6];
    const hit = common.find((p) => Math.abs(p - approx) < 0.02);
    if (hit) return hit;
  }
  return null;
}

const ADJUSTMENT_CODES: Array<{ code: string; label: string; sublabel: string }> = [
  { code: 'MAXLOANAMT_CAP', label: '50% income cap', sublabel: 'Legacy floor-to-$5 rule' },
  { code: 'EI_CAP', label: 'EI income cap', sublabel: 'Employment insurance maximum' },
  { code: 'GENERAL_CAP', label: 'Product cap', sublabel: 'General $1,000 maximum' },
  { code: 'AMOUNT_DUE_REDUCE', label: 'Open balance reduction', sublabel: 'Reduced by amount currently due' },
];

/**
 * Legacy Cash4You pre-approval table calculation only (no bank affordability).
 * Built from DE affordabilityTrace + remarks.
 */
export function buildPdlLegacyGridCalculation(
  pdl?: PdlAffordabilityJourneyDto | null,
): PdlLegacyGridCalculationModel | null {
  const trace = pdl?.affordabilityTrace ?? [];
  if (trace.length === 0) return null;

  const table = readTraceNumber(traceStep(trace, 'SCORING_TABLE'));
  const band = readTraceNumber(traceStep(trace, 'INCOME_RANGE'));
  const tier = readTraceNumber(traceStep(trace, 'LOAN_CATEGORY'));
  const monthlyIncome =
    readTraceNumber(traceStep(trace, 'LEGACY_INCOME')) ?? pdl?.monthlyIncome ?? null;
  const gridRaw = readTraceNumber(traceStep(trace, 'GRID_RAW'));
  const afterCellMax = readTraceNumber(traceStep(trace, 'GRID_CAPPED'));
  const legacyLimit = readTraceNumber(traceStep(trace, 'LEGACY_PRA_LIMIT'));

  if (table == null && band == null && legacyLimit == null && gridRaw == null) {
    return null;
  }

  const loanPct = parseLoanPct(pdl?.remarks, monthlyIncome, gridRaw);
  const loanPctLabel = loanPct != null ? `${Math.round(loanPct * 100)}%` : null;

  const profileChips: string[] = [];
  if (table != null) {
    const desc = SCORING_TABLE_DESCRIPTIONS[table];
    profileChips.push(desc ? `Table ${table} · ${desc}` : `Table ${table}`);
  }
  if (band != null && INCOME_RANGE_LABELS[band]) {
    profileChips.push(`Band ${band} · ${INCOME_RANGE_LABELS[band]}`);
  }
  if (tier != null && LOAN_CATEGORY_LABELS[tier]) {
    profileChips.push(`Tier ${tier} · ${LOAN_CATEGORY_LABELS[tier]}`);
  }

  const bars: PdlLegacyCalcBar[] = [];
  const amounts: number[] = [];

  if (monthlyIncome != null && monthlyIncome > 0) {
    bars.push({
      id: 'income',
      label: 'Verified monthly income',
      sublabel: 'Input to the scoring table',
      amount: monthlyIncome,
      kind: 'income',
      binding: false,
    });
    amounts.push(monthlyIncome);
  }

  if (gridRaw != null && gridRaw > 0) {
    bars.push({
      id: 'raw',
      label: 'Income × loan %',
      sublabel: loanPctLabel
        ? `Rounded up to nearest $10 (${loanPctLabel} of income)`
        : 'Rounded up to nearest $10',
      amount: gridRaw,
      kind: 'raw',
      binding: false,
    });
    amounts.push(gridRaw);
  }

  if (
    afterCellMax != null &&
    afterCellMax > 0 &&
    (gridRaw == null || afterCellMax < gridRaw)
  ) {
    bars.push({
      id: 'cell_cap',
      label: 'After scoring-table cell max',
      sublabel: 'min(raw calculation, max_loan_amt for this cell)',
      amount: afterCellMax,
      kind: 'cell_cap',
      binding: legacyLimit != null && legacyLimit === afterCellMax,
    });
    amounts.push(afterCellMax);
  }

  let lastAmount = afterCellMax ?? gridRaw ?? null;
  for (const adj of ADJUSTMENT_CODES) {
    const step = traceStep(trace, adj.code);
    const value = readTraceNumber(step);
    if (value == null || value <= 0) continue;
    if (lastAmount != null && value >= lastAmount) continue;
    bars.push({
      id: adj.code,
      label: adj.label,
      sublabel: adj.sublabel,
      amount: value,
      kind: 'adjustment',
      binding: false,
    });
    amounts.push(value);
    lastAmount = value;
  }

  const finalLimit = legacyLimit ?? lastAmount;
  const lastBar = bars[bars.length - 1];
  const duplicateFinal =
    lastBar != null && finalLimit != null && lastBar.amount === finalLimit && lastBar.binding;
  if (finalLimit != null && finalLimit >= 0 && !duplicateFinal) {
    bars.push({
      id: 'result',
      label: 'Pre-approval table limit',
      sublabel: 'Legacy Cash4You grid result',
      amount: finalLimit,
      kind: 'result',
      binding: true,
    });
    amounts.push(finalLimit);
  } else if (duplicateFinal && lastBar) {
    lastBar.kind = 'result';
    lastBar.label = 'Pre-approval table limit';
    lastBar.sublabel = 'Legacy Cash4You grid result';
  }

  if (bars.length === 0) return null;

  let formulaLine: string | null = null;
  if (monthlyIncome != null && loanPctLabel && gridRaw != null) {
    formulaLine = `${fmt(monthlyIncome)} × ${loanPctLabel} → ${fmt(gridRaw)}`;
    if (afterCellMax != null && afterCellMax < gridRaw) {
      formulaLine += ` → cell max ${fmt(afterCellMax)}`;
    }
  }

  return {
    monthlyIncome,
    scoringTable: table,
    incomeBand: band,
    customerTier: tier,
    profileChips,
    loanPct,
    loanPctLabel,
    gridRaw,
    afterCellMax,
    legacyLimit: finalLimit,
    bars,
    scaleMax: Math.max(
      ...bars.filter((b) => b.kind !== 'income').map((b) => b.amount),
      100,
    ),
    formulaLine,
  };
}
